import { Component, EventEmitter, Inject, Input, OnChanges, Output, ViewChild } from "@angular/core";
import { PeopleService, UsersById } from "@app2/account/people.service";
import { MultipleAlertStateChangerComponent } from "@app2/alert/alert-inbox/multiple-alert-state-changer.component";
import {
    getMatchedThreatValue,
    getOverallScoreForUbaAlert,
    queryStringForAlertResult
} from "@app2/alert/alert-inbox/utils";
import { Domain, FRAUD_DOMAIN } from "@app2/alert/constants";
import { AlertClientService } from "@app2/clients/alert-client.service";
import { ThreatClientService } from "@app2/clients/threat-client.service";
import {
    Alert,
    AlertStateChange,
    AlertType,
    AnomalyAlertResult,
    QueryTrigger,
    SequenceTriggerAlertResult,
    SequenceTriggerAlertResultWithId,
    TriggerType,
    UbaAlertResult,
} from "@app2/clients/types/alert-typedefs";
import { PRODUCT_DOMAIN } from "@app2/alert/alert-inbox/injection-tokens";
import { StateType } from "@app2/fraud/type-defs";
import { SavedQueryService } from "@app2/search/saved-query.service";
import { NotificationsService } from "@app2/shared/services/notifications.service";
import { RouterService } from "@app2/shared/services/router.service";
import { TimeService } from "@app2/shared/services/time.service";
import { DEFAULT_PAGE_SIZE } from "@app2/shared/shared-constants";
import { BeginEndRange } from "@app2/shared/type-defs";
import { SavedQuery } from "@app2/type-defs/search/search-types";
import { ThreatGroup } from "@app2/type-defs/threat/threat-types";
import { getMessageForError } from "@app2/util/errors/handleable-errors";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Observable } from "rxjs";
import { shareReplay } from "rxjs/operators";
import * as _ from "underscore";
import { getBeginDate, getEndDate } from "../../fraud/utils";

interface ThreatGroupWithText extends ThreatGroup {
    infoText: string;
}

@UntilDestroy()
@Component({
    selector: "trigger-alerts-table",
    template: `
        <div *dsLoading="loading">
            <div class="flex-container alert-table-header">
                <span class="alert-table-title">{{ "RECENT_ALERTS" | localize }}</span>
                <multiple-alert-state-changer class="multi-alert-state-changer"
                                              *ngIf="trigger && state !== 'ESCALATED'"
                                              [currentState]="state"
                                              [resultTimestamp]="firstAlertTimestamp"
                                              [trigger]="trigger"
                                              (onStateChanged)="onAlertsStateChanged.emit($event)"
                                              (alertStateChangeFailure)="retryChangeAlertState($event)">
                </multiple-alert-state-changer>
            </div>
            <table class="hover-table full-width alerts-table">
                <thead class="alert-table-column-headers">
                <tr>
                    <th class="hover-tab"></th>
                    <th class="no-right-padding fraud-inbox-trigger-view-bell-column"></th>

                    <!-- these are mutually exclusive; sequence and threat triggers dont have individual queries -->
                    <th *ngIf="(savedQuery$ | async)?.primaryField" class="no-left-padding medium-cell">
                        {{ "GROUP_BY" | localize }}: {{ (savedQuery$ | async).primaryField | uppercase }}
                    </th>
                    <th *ngIf="trigger.type === TriggerType.sequence" class="no-left-padding medium-cell">
                        {{ "MATCHED_VALUES" | localize | uppercase }}
                    </th>
                    <th *ngIf="trigger.type === TriggerType.ueba" class="no-left-padding medium-cell">
                        {{ "USER_ENTITY" | localize | uppercase }}
                    </th>
                    <ng-container *ngIf="trigger.type === TriggerType.fraudUba">
                        <th class="no-left-padding medium-cell">
                            {{ "USER" | localize }}
                        </th>
                        <th class="no-left-padding long-cell">
                            {{ "RISK_FACTORS" | localize }}
                        </th>
                        <th class="no-left-padding medium-cell">
                            {{ "OVERALL_SCORE" | localize }}
                        </th>
                        <th class="no-left-padding timestamp-cell">
                            {{ "DURING" | localize }}
                        </th>
                    </ng-container>
                    <th *ngIf="trigger.type === TriggerType.threat" class="no-left-padding medium-cell">
                        {{ 'MATCHED_THREAT' | localize}}
                    </th>
                    <th *ngIf="trigger.type === TriggerType.custom" class="short-cell center">
                        {{ "COUNT" | localize}}
                    </th>
                    <th *ngIf="trigger.type === TriggerType.threat" class="medium-cell">
                        {{ "THREAT_UPLOAD" | localize}}
                    </th>
                    <th *ngIf="trigger.type !== TriggerType.patternscoutSmartThreshold && trigger.type !== TriggerType.ueba
                         && trigger.type !== TriggerType.fraudUba"
                        class="timestamp-cell center">
                        {{ (trigger.type === TriggerType.threat ? "CREATED_ON" : "INTERVAL") | localize }}
                    </th>

                    <ng-container *ngIf="trigger.type === TriggerType.patternscoutSmartThreshold">
                        <th class="no-left-padding medium-cell">
                            {{ "ANOMALY" | localize }} <span
                                class="no-text-transform">({{ "ACTUAL_VERSUS_EXPECTED" | localize }})</span>
                        </th>
                        <th class="no-left-padding timestamp-cell">
                            {{ "DURING" | localize }}
                        </th>
                    </ng-container>
                    <ng-container *ngIf="trigger.type === TriggerType.ueba">
                        <th class="no-left-padding medium-cell">
                            {{ "RISK_FACTORS" | localize }}
                        </th>
                        <th class="no-left-padding timestamp-cell">
                            {{ "DURING" | localize }}
                        </th>
                    </ng-container>
                    <th class="short-cell"></th>
                </tr>
                </thead>
                <tbody *ngIf="alerts.length">
                <tr *ngFor="let alert of alerts"
                    class="declutter"
                    test-class="alert"
                    [attr.test-id]="alert.id">
                    <td class="hover-tab"></td>
                    <td class="no-right-padding fraud-inbox-trigger-view-bell-column">
                        <a (click)="showAlertView(alert)"
                           class="link-text alert-view-link"
                           id="alert-link"
                           test-class="view-alert">
                            <i class="far fa-bell show-on-hover"
                               [matTooltip]="'INBOX_VIEW_ALERT' | localize"></i>
                        </a>
                    </td>
                    <td class="medium-cell"
                        *ngIf="(savedQuery$ | async)?.primaryField || trigger.type === TriggerType.ueba ||
                        trigger.type === TriggerType.fraudUba"
                        test-class="alert-text">
                        <a *ngIf="alert.primaryTerm"
                           [matTooltip]="'INBOX_VIEW_STORY' | localize"
                           class="link-text no-underline story-view-link"
                           dsRouterLink="."
                           [queryParams]="{ mode: 'alert_story', selected: alert.primaryTerm, state: state }"
                           queryParamsHandling="merge"
                           test-class="view-alert-story">
                            {{ alert.primaryTerm }}
                        </a>
                    </td>
                    <td class="medium-cell" *ngIf="trigger.type === TriggerType.sequence">
                        <ng-container *ngFor="let matchedValue of getMatches(alert)">
                            {{ matchedValue }}
                            <a class="link-text no-underline story-view-link"
                               dsRouterLink="."
                               [queryParams]="{ mode: 'alert_story', selected: matchedValue }"
                               queryParamsHandling="merge"
                               test-class="view-alert-story">
                                <i class="fa fa-list show-on-hover"
                                   [matTooltip]="'INBOX_VIEW_STORY' | localize"></i>
                            </a>
                        </ng-container>
                    </td>
                    <td class="medium-cell" *ngIf="trigger.type === TriggerType.threat"
                        test-class="alert-text">
                        <a [dsRouterLink]="['/search']"
                           [queryParams]="{query: getMatchedThreatValue(alert),
                                           begin: threatAlertSearchRanges[alert.id].begin,
                                           end: threatAlertSearchRanges[alert.id].end}"
                           target="_blank"
                           test-class="threat-match-value-link">
                            {{ getMatchedThreatValue(alert) }}
                        </a>
                        <a class="link-text no-underline story-view-link"
                           dsRouterLink="."
                           [queryParams]="{ mode: 'alert_story', selected: getMatchedThreatValue(alert) }"
                           queryParamsHandling="merge"
                           test-class="view-alert-story">
                            <i class="fa fa-list show-on-hover"
                               [matTooltip]="'INBOX_VIEW_STORY' | localize"></i>
                        </a>
                    </td>
                    <td *ngIf="trigger.type === TriggerType.threat" class="medium-cell center">
                        <ng-container *ngIf="!threatGroups[alert.threatMatchId]">
                            <i class="fa fa-spinner fa-spin"></i>
                            {{ 'LOADING' | localize }}
                        </ng-container>
                        <div class="threat-group-container" *ngIf="threatGroups[alert.threatMatchId]">
                            <span class="interval-container de-emphasize" test-class="threat-group-upload-date">
                                {{ threatGroups[alert.threatMatchId].createdOn | shortDateAndTime }}
                            </span>
                            <i *ngIf="threatGroups[alert.threatMatchId].createdBy ||
                                      threatGroups[alert.threatMatchId].note"
                               class="far fa-info-circle info-icon" test-class="threat-group-info-text"
                               [matTooltip]="threatGroups[alert.threatMatchId].infoText"
                               [matTooltipClass]="'pre-line'">
                            </i>
                        </div>
                    </td>
                    <td *ngIf="trigger.type === TriggerType.custom"
                        class="short-cell center"
                        test-class="search-link-column">
                        <search-link
                                [queryString]="queryStringForAlertResult(trigger, savedQuery$ | async, alert)"
                                [filterField]="trigger.filterField"
                                [filterListId]="trigger.filterListId"
                                [excludeField]="trigger.excludeField"
                                [excludeListId]="trigger.excludeListId"
                                [begin]="alert.resultTimestamp"
                                [end]="alert.resultEndTimestamp"
                                [defaultText]="false"
                                [newTab]="true"
                                [matTooltip]="'INBOX_VIEW_EVENTS' | localize">
                            {{ alert.resultCount }}
                        </search-link>
                    </td>
                    <ng-container *ngIf="trigger.type === TriggerType.ueba">
                        <td class="medium-cell center">
                            <ul class="risk-factor-list">
                                <li class="risk-factor-list" *ngFor="let reasonLine of alert.reason">
                                    <a class="link-text no-underline"
                                       test-class="reason"
                                       href="{{reasonLine | mlAlertReasonLink}}">{{reasonLine | mlAlertReason}}</a>
                                </li>
                            </ul>
                        </td>
                    </ng-container>
                    <ng-container *ngIf="trigger.type === TriggerType.fraudUba">
                        <td class="long-cell center">
                            <ul class="risk-factor-list">
                                <li *ngFor="let riskFactor of alert.riskFactors" test-class="risk-factor">
                                    {{ "UBA_RISK_FACTOR_" + riskFactor.name | localize }}
                                </li>
                            </ul>
                        </td>
                        <td class="medium-cell center overall-score" test-class="overall-score">
                            {{ getOverallScoreForUbaAlert(alert) }}
                        </td>
                    </ng-container>
                    <td *ngIf="trigger.type !== TriggerType.patternscoutSmartThreshold"
                        class="timestamp-cell center"
                        test-class="alert-timestamp">
                        <span *ngIf="trigger.type !== TriggerType.threat"
                              class="de-emphasize">
                            <span class="interval-container">{{ alert.resultTimestamp | shortDateAndTime }}</span>
                            &ndash;
                            <span class="interval-container">{{ alert.resultEndTimestamp | shortDateAndTime }}</span>
                        </span>
                        <span class="de-emphasize"
                              *ngIf="trigger.type === TriggerType.threat">
                            {{ alert.resultTimestamp | shortDateAndTime }}
                        </span>
                    </td>
                    <ng-container *ngIf="trigger.type === TriggerType.patternscoutSmartThreshold">
                        <td class="medium-cell center">
                            <anomaly-counts [trigger]="trigger" [alert]="alert"></anomaly-counts>
                        </td>
                        <td class="timestamp-cell center"
                            [matTooltip]="'INTERVAL_ALERT_MESSAGE'| localize : (alert.resultEndTimestamp | shortDateAndTime)">
                            <span class="de-emphasize">
                                <span class="interval-container"
                                      test-class="anomaly-start-date-time">{{ alert.anomalyTimestamp | shortDateAndTime }}</span>
                                &ndash;
                                <span class="interval-container"
                                      test-class="anomaly-end-date-time">{{ alert.anomalyEndTimestamp | shortDateAndTime }}</span>
                            </span>
                        </td>
                    </ng-container>
                    <td class="short-cell center show-on-hover">
                        <single-alert-state-changer *ngIf="alert.currentState !== 'ESCALATED'"
                                                    (onStateChanged)="refreshAlerts()"
                                                    [alertId]="alert.id"
                                                    [currentState]="alert.currentState"
                                                    [resultTimestamp]="alert.resultTimestamp"
                                                    [trigger]="trigger">
                        </single-alert-state-changer>
                    </td>
                </tr>
                <tr *ngIf="canLoadMoreAlerts">
                    <td colspan="7"
                        id="load-more-alerts">
                        <ng-container *ngIf="loadingMoreAlerts">
                            <i class="fa fa-spinner fa-spin"></i>
                            {{ 'LOADING' | localize }}
                        </ng-container>
                        <a *ngIf="!loadingMoreAlerts"
                           (click)="loadMoreAlerts()"
                           class="link-text">{{ 'LOAD_MORE' | localize }}</a>
                    </td>
                </tr>
                </tbody>
                <tbody *ngIf="!alerts?.length">
                <tr>
                    <td class="no-data-text" colspan="7">
                        {{ 'INBOX_NO_ALERTS_FOR_TRIGGER' | localize }}
                    </td>
                </tr>
                </tbody>
            </table>
        </div>
    `,
    styles: [`
        @import "/src/styles/colors";
        @import "/src/styles/settings";

        .multi-alert-state-changer {
            margin-left: auto;
        }

        .alert-table-title {
            font-size: 16px;
            font-weight: $font-weight-bold;
        }

        .hover-tab {
            width: 0.15rem;
            min-width: 0.15rem;
        }

        .info-icon {
            color: $gray-7;

            &:hover {
                color: $ds-blue-6;
            }
        }

        .threat-group-container {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 164px;
        }

        .overall-score {
            font-weight: $font-weight-bold;
            font-size: 0.75rem;
        }
    `],
})
export class TriggerAlertsTableComponent implements OnChanges {
    TriggerType = TriggerType;

    @Input() trigger: QueryTrigger;
    @Input() state: StateType;
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onAlertsStateChanged = new EventEmitter<number>();
    @ViewChild(MultipleAlertStateChangerComponent) multipleAlertStateChanger;

    firstAlertTimestamp: date;
    alerts: AlertType[] = [];

    users: UsersById;
    threatGroups: Record<string, ThreatGroupWithText> = {};
    threatAlertSearchRanges: Record<uuid, BeginEndRange> = {};
    loading: boolean;
    loadingMoreAlerts: boolean;
    canLoadMoreAlerts: boolean;
    savedQuery$: Observable<SavedQuery>;

    constructor(@Inject(PRODUCT_DOMAIN) private inboxDomain: Domain,
                private alertService: AlertClientService,
                private notificationsService: NotificationsService,
                private savedQueryService: SavedQueryService,
                private routerService: RouterService,
                private readonly time: TimeService,
                private threatService: ThreatClientService,
                private peopleService: PeopleService) {
    }

    ngOnChanges() {
        if (this.trigger && this.state) {
            this.refreshAlerts();
            this.savedQuery$ = this.trigger.savedQueryId
                ? this.savedQueryService.getSavedQuery$(this.trigger.savedQueryId)
                    .pipe(shareReplay(1))
                : null;
        }
    }

    getMatches(alert: SequenceTriggerAlertResultWithId): string[] {
        return _.flatten(alert.stepResults.map(step => step.matchValues.map(mv => mv.value)));
    }

    /* This function is called if the call to the changeAlertStateForTrigger endpoint in alert service fails
       in multiple-alert-state-changed.component.ts
    */
    retryChangeAlertState(stateChange: AlertStateChange) {
        this.alerts = [];
        this.loadMoreAlerts().then(() => {
            this.firstAlertTimestamp = this.alerts[0]?.resultTimestamp;
            //This check is needed or else retry mechanism will keep being called forever
            if (this.firstAlertTimestamp) {
                const promise = this.multipleAlertStateChanger.changeAlertState(stateChange, this.firstAlertTimestamp);
                if (promise) {
                    this.multipleAlertStateChanger.sendAlertCount(promise);
                }
            }
        });
    }

    refreshAlerts() {
        this.loading = true;
        this.alerts = [];
        this.loadMoreAlerts()
            .then(() => this.firstAlertTimestamp = this.alerts[0]?.resultTimestamp)
            .finally(() => this.loading = false);
    }

    loadMoreAlerts(): Promise<any> {
        this.loadingMoreAlerts = true;
        const filters = [`triggerId:${this.trigger.id}`, `state:${ this.state }`];

        let loadPagePromise: Promise<AlertType[]>;

        if (this.trigger.type === TriggerType.patternscoutSmartThreshold) {
            loadPagePromise = this.alertService.getAnomalyAlertPage(DEFAULT_PAGE_SIZE, filters, (this.alerts[this.alerts.length - 1] as AnomalyAlertResult))
                .then(alertsWithoutIds => alertsWithoutIds.map(alert => ({
                    ...alert,
                    id: alert.alertId,
                })));
        } else if (this.trigger.type === TriggerType.sequence) {
            loadPagePromise = this.alertService
                .getSequenceAlertPage(DEFAULT_PAGE_SIZE, filters, (this.alerts[this.alerts.length - 1] as SequenceTriggerAlertResult))
                .then(alertsWithoutId => alertsWithoutId.map(alert => ({
                    ...alert,
                    id: alert.alertId,
                })));
        } else if (this.trigger.type === TriggerType.ueba) {
            loadPagePromise = this.alertService.getAnomalyAlertPage(DEFAULT_PAGE_SIZE, filters, (this.alerts[this.alerts.length - 1] as AnomalyAlertResult))
                .then(alertsWithoutReason => alertsWithoutReason.map(alert => ({
                    ...alert,
                    reason: alert.reason,
                    id: alert.alertId,
                })));
        } else if (this.trigger.type === TriggerType.fraudUba) {
            loadPagePromise = this.alertService.getUbaAlertPage(DEFAULT_PAGE_SIZE, filters, (this.alerts[this.alerts.length - 1] as UbaAlertResult))
                .then(alertsWithoutId => alertsWithoutId.map(alert => ({
                    ...alert,
                    id: alert.alertId,
                })));
        } else {
            loadPagePromise = this.alertService
                .getAlertPage(true, "TIMESTAMP", this.inboxDomain, filters, (this.alerts[this.alerts.length - 1] as Alert))
                .then(alerts => {
                    if (this.trigger.type === TriggerType.threat) {
                        let threatMatchIds = alerts.map(alert => alert.threatMatchId);
                        this.threatService.getThreatGroupInfoForThreatMatches(threatMatchIds)
                            .then(response => {
                                let threatGroupMap = response.threatGroupsByThreatMatchId;
                                let ids = Object.keys(threatGroupMap);
                                if (!this.users) {
                                    this.peopleService.getAllUsersById$()
                                        .pipe(untilDestroyed(this))
                                        .subscribe(users => {
                                            this.users = users;
                                            this.setThreatGroups(ids, threatGroupMap);
                                        });
                                } else {
                                    this.setThreatGroups(ids, threatGroupMap);
                                }
                            })

                            .catch(error => {
                                const message = getMessageForError(error.error, "ERROR_LOADING_THREAT_GROUP_INFO");
                                this.notificationsService.showError(message, error);
                            });
                        alerts.forEach(alert => {
                            let startDate = getBeginDate(alert.resultTimestamp, this.time);
                            let endDate = getEndDate(alert.resultTimestamp, this.time);
                            this.threatAlertSearchRanges[alert.id] = {begin: startDate, end: endDate};
                        });
                    }
                    return alerts;
                });
        }

        return loadPagePromise
            .then(newAlerts => {
                this.alerts = [...this.alerts, ...newAlerts as any[]];
                this.canLoadMoreAlerts = newAlerts.length === DEFAULT_PAGE_SIZE;
            })
            .catch(error => {
                const errorMessage = getMessageForError(error.error, "ERROR_LOADING_ALERTS");
                this.notificationsService.showError(errorMessage, error);
            })
            .finally(() => this.loadingMoreAlerts = false);
    }

    showAlertView(alert) {
        if (this.inboxDomain === FRAUD_DOMAIN) {
            this.routerService.navigate(["/fraud/inbox"], {
                mode: "alert",
                dropdownFilter: "all",
                sort: "newest_first",
                state: this.state,
                selected: alert.id,
            }, {}, { openInNewTab: false });
        } else {
            this.routerService.mergeParams({ mode: "alert", selected: alert.id });
        }
    }

    setThreatGroups(ids: string[], threatGroupMap: Record<number, ThreatGroup>) {
        ids.forEach(id => {
            let grp = threatGroupMap[id];
            if (grp.createdBy) {
                grp.infoText = this.getThreatGroupInfoText(grp,
                    this.users[grp.createdBy].fullName);
            }
            this.threatGroups[id] = grp;
        });
    }

    getThreatGroupInfoText(threatGroup: ThreatGroup, creatorName: string): string {
        let textStart = "Uploaded by " + creatorName;
        return threatGroup.note && threatGroup.note.trim() !== ""
               ? textStart + "\n\nReason:\n" + threatGroup.note : textStart;
    }

    queryStringForAlertResult = queryStringForAlertResult;
    getMatchedThreatValue = getMatchedThreatValue;
    getBeginDate = getBeginDate;
    getEndDate = getEndDate;
    reasonLine: string;
    protected readonly getOverallScoreForUbaAlert = getOverallScoreForUbaAlert;
}
